import { z } from "zod";

/**
 * The collaborative generation flow's phase vocabulary.
 *
 * Nothing here is control-specific: the backend's phases come from one enum
 * (`CollabGenerationPhase`), so the wire values, their order, and their labels
 * are the same for any entity that runs the flow. Only two descriptions name
 * what the flow produces, which is why `collabPhaseDisplay` takes them and
 * nothing else.
 */

export const CollabPhaseSchema = z.enum([
  "understanding",
  "planning",
  "iterating",
  "generating",
  "complete",
  "failed",
]);
export type CollabPhase = z.infer<typeof CollabPhaseSchema>;

/**
 * Phases in progress-indicator order. `failed` is absent by design: it is not a
 * step along the way, it is where a run stopped. A failed run never reaches the
 * stepper at all - the wizard swaps the whole page for a "Session Failed" card
 * carrying `error_message` and the retry actions.
 */
export const PHASE_ORDER: CollabPhase[] = [
  "understanding",
  "planning",
  "iterating",
  "generating",
  "complete",
];

export const PHASE_LABELS: Record<CollabPhase, string> = {
  understanding: "Understanding",
  planning: "Planning",
  iterating: "Iterating",
  generating: "Generating",
  complete: "Complete",
  failed: "Failed",
};

export interface CollabPhaseDisplay {
  label: string;
  description: string;
}

/**
 * Build an entity's phase-display record.
 *
 * Every label and four of the six descriptions are the same for any entity; the
 * two below are the only copy that names what is being built.
 */
export const collabPhaseDisplay = (copy: {
  /** While generating — e.g. "Creating final control". */
  generating: string;
  /** Once finished — e.g. "Control created". */
  complete: string;
}): Record<CollabPhase, CollabPhaseDisplay> => ({
  understanding: {
    label: PHASE_LABELS.understanding,
    description: "Analyzing your documents",
  },
  planning: {
    label: PHASE_LABELS.planning,
    description: "Creating draft plan",
  },
  iterating: {
    label: PHASE_LABELS.iterating,
    description: "Ready for your feedback",
  },
  generating: {
    label: PHASE_LABELS.generating,
    description: copy.generating,
  },
  complete: {
    label: PHASE_LABELS.complete,
    description: copy.complete,
  },
  failed: {
    label: PHASE_LABELS.failed,
    description: "An error occurred",
  },
});
